
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
}

interface LoanRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: LoanFormData) => Promise<boolean>;
  employees: Employee[];
  isAdmin?: boolean;
  currentUserEmployeeId?: string | null;
}

export interface LoanFormData {
  employeeId: string;
  amount: number;
  installmentAmount: number;
  purpose: string;
  notes: string;
  monthsForPayment: number;
  startMonth: string;
}

export const LoanRequestForm = ({ isOpen, onClose, onSubmit, employees, isAdmin = true, currentUserEmployeeId = null }: LoanRequestFormProps) => {
  const [formData, setFormData] = useState<LoanFormData>({
    employeeId: "",
    amount: 0,
    installmentAmount: 0,
    purpose: "",
    notes: "",
    monthsForPayment: 1,
    startMonth: `${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}`,
  });
  const { toast } = useToast();

  // Auto-select current user's employee ID when form opens for non-admin users
  useEffect(() => {
    if (isOpen && !isAdmin && currentUserEmployeeId) {
      setFormData(prev => ({
        ...prev,
        employeeId: currentUserEmployeeId
      }));
    } else if (isOpen && isAdmin) {
      // Reset employee selection for admin when form opens
      setFormData(prev => ({
        ...prev,
        employeeId: ""
      }));
    }
  }, [isOpen, isAdmin, currentUserEmployeeId]);

  const handleChange = (field: keyof LoanFormData, value: string | number) => {
    const newFormData = { ...formData, [field]: value };
    
    // Auto-calculate monthly installment when amount or months change
    if (field === 'amount' || field === 'monthsForPayment') {
      const amount = field === 'amount' ? (value as number) : newFormData.amount;
      const months = field === 'monthsForPayment' ? (value as number) : newFormData.monthsForPayment;
      
      if (amount > 0 && months > 0) {
        newFormData.installmentAmount = Math.round((amount / months) * 100) / 100; // Round to 2 decimal places
      }
    }
    
    setFormData(newFormData);
  };

  const handleSubmit = async () => {
    if (!formData.employeeId) {
      toast({
        title: "Error",
        description: "Please select an employee",
        variant: "destructive",
      });
      return;
    }
    
    if (formData.amount <= 0) {
      toast({
        title: "Error",
        description: "Advance amount must be greater than zero",
        variant: "destructive",
      });
      return;
    }

    if (formData.monthsForPayment <= 0) {
      toast({
        title: "Error",
        description: "Number of months for payment must be greater than zero",
        variant: "destructive",
      });
      return;
    }

    if (!formData.startMonth) {
      toast({
        title: "Error",
        description: "Please select a start month for payment",
        variant: "destructive",
      });
      return;
    }

    const success = await onSubmit(formData);
    if (success) {
      setFormData({
        employeeId: "",
        amount: 0,
        installmentAmount: 0,
        purpose: "",
        notes: "",
        monthsForPayment: 1,
        startMonth: `${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}`,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>New Advance Request</DialogTitle>
          <DialogDescription>
            {isAdmin ? 'Create a new advance request for an employee' : 'Submit a new advance request for yourself'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4 overflow-y-auto flex-1">
          {isAdmin ? (
            <div className="space-y-2">
              <Label htmlFor="employee">Employee</Label>
              <Select
                value={formData.employeeId}
                onValueChange={(value) => handleChange("employeeId", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="employee">Employee</Label>
              <Input
                id="employee"
                value={currentUserEmployeeId ? employees.find(emp => emp.id === currentUserEmployeeId) 
                  ? `${employees.find(emp => emp.id === currentUserEmployeeId)?.firstName} ${employees.find(emp => emp.id === currentUserEmployeeId)?.lastName}`
                  : 'Your Account' : 'Your Account'}
                disabled
                className="bg-gray-50"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="advanceAmount">Advance Amount ($)</Label>
            <Input
              id="advanceAmount"
              type="number"
              min="0"
              value={formData.amount || ""}
              onChange={(e) => handleChange("amount", parseFloat(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="monthsForPayment">Number of Months for Payment</Label>
            <Input
              id="monthsForPayment"
              type="number"
              min="1"
              max="24"
              value={formData.monthsForPayment || ""}
              onChange={(e) => handleChange("monthsForPayment", parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="installmentAmount">Monthly Installment ($)</Label>
            <Input
              id="installmentAmount"
              type="number"
              min="0"
              value={formData.installmentAmount || ""}
              onChange={(e) => handleChange("installmentAmount", parseFloat(e.target.value) || 0)}
              disabled
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500">Auto-calculated based on advance amount and months</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="startMonth">Start Month for Payment</Label>
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={formData.startMonth ? formData.startMonth.split('-')[0] : ''}
                onValueChange={(year) => {
                  const month = formData.startMonth ? formData.startMonth.split('-')[1] : '01';
                  handleChange("startMonth", `${year}-${month}`);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select
                value={formData.startMonth ? formData.startMonth.split('-')[1] : ''}
                onValueChange={(month) => {
                  const year = formData.startMonth ? formData.startMonth.split('-')[0] : new Date().getFullYear().toString();
                  handleChange("startMonth", `${year}-${month}`);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => {
                    const month = (i + 1).toString().padStart(2, '0');
                    const monthName = new Date(2024, i).toLocaleString('default', { month: 'long' });
                    return (
                      <SelectItem key={month} value={month}>
                        {monthName}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="purpose">Purpose</Label>
            <Input
              id="purpose"
              value={formData.purpose}
              onChange={(e) => handleChange("purpose", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            className="bg-payroll-primary hover:bg-blue-700"
            onClick={handleSubmit}
          >
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
